/**
 * Living Expenses Panel (SPRINT-6B F007)
 *
 * CRUD panel for managing contact-level living expenses.
 * Shows a table of expense entries with add/edit/delete actions,
 * plus a totals row summarising annual expenses.
 *
 * States:
 * - Empty: No expenses, shows "No living expenses recorded" with Add button
 * - Table: Shows expenses with columns and summary totals
 * - Modal: Add/Edit form with live annual calculation
 * - Delete: AlertDialog confirmation
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Loader2, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Types
interface LivingExpenseEntry {
  id: string;
  contactId: string;
  expenseCategory: string;
  description: string | null;
  amount: number;
  frequency: string;
  annualAmount: number;
  isJointExpense: boolean;
  hemApplicable: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FormState {
  expenseCategory: string;
  description: string;
  amount: string;
  frequency: string;
  isJointExpense: boolean;
  hemApplicable: boolean;
}

export interface LivingExpensesPanelProps {
  contactId: string;
}

// Constants
const EXPENSE_CATEGORY_OPTIONS = [
  { value: 'housing', label: 'Housing' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'transport', label: 'Transport' },
  { value: 'food_groceries', label: 'Food & Groceries' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'education_childcare', label: 'Education & Childcare' },
  { value: 'telecommunications', label: 'Telecommunications' },
  { value: 'health_medical', label: 'Health & Medical' },
  { value: 'personal_lifestyle', label: 'Personal & Lifestyle' },
  { value: 'child_support_alimony', label: 'Child Support / Alimony' },
  { value: 'other', label: 'Other' },
];

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

const FREQUENCY_MULTIPLIERS: Record<string, number> = {
  weekly: 52,
  fortnightly: 26,
  monthly: 12,
  quarterly: 4,
  annually: 1,
};

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  EXPENSE_CATEGORY_OPTIONS.map(o => [o.value, o.label])
);

const FREQUENCY_LABELS: Record<string, string> = Object.fromEntries(
  FREQUENCY_OPTIONS.map(o => [o.value, o.label])
);

// Format currency
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

const defaultFormState: FormState = {
  expenseCategory: 'housing',
  description: '',
  amount: '',
  frequency: 'monthly',
  isJointExpense: false,
  hemApplicable: true,
};

export const LivingExpensesPanel: React.FC<LivingExpensesPanelProps> = ({ contactId }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<LivingExpenseEntry | null>(null);
  const [deleteEntry, setDeleteEntry] = useState<LivingExpenseEntry | null>(null);
  const [formState, setFormState] = useState<FormState>(defaultFormState);

  // Fetch living expenses
  const { data: entries = [], isLoading } = useQuery<LivingExpenseEntry[]>({
    queryKey: [`/api/crm/contacts/${contactId}/living-expenses`],
    enabled: !!contactId,
  });

  // Live calculation of annual amount
  const calculatedAnnual = useMemo(() => {
    const amount = parseFloat(formState.amount) || 0;
    const multiplier = FREQUENCY_MULTIPLIERS[formState.frequency] || 1;
    return amount * multiplier;
  }, [formState.amount, formState.frequency]);

  // Sync form state when editing
  useEffect(() => {
    if (editingEntry) {
      setFormState({
        expenseCategory: editingEntry.expenseCategory,
        description: editingEntry.description || '',
        amount: String(editingEntry.amount),
        frequency: editingEntry.frequency,
        isJointExpense: editingEntry.isJointExpense,
        hemApplicable: editingEntry.hemApplicable,
      });
    }
  }, [editingEntry]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('POST', `/api/crm/contacts/${contactId}/living-expenses`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/contacts/${contactId}/living-expenses`] });
      handleCloseModal();
      toast({ title: 'Expense added', description: 'Living expense has been created' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to create expense', variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest('PATCH', `/api/crm/contacts/${contactId}/living-expenses/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/contacts/${contactId}/living-expenses`] });
      handleCloseModal();
      toast({ title: 'Expense updated', description: 'Living expense has been updated' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to update expense', variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/crm/contacts/${contactId}/living-expenses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/contacts/${contactId}/living-expenses`] });
      setDeleteEntry(null);
      toast({ title: 'Expense deleted', description: 'Living expense has been removed' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete expense', variant: 'destructive' });
    },
  });

  const handleOpenAdd = () => {
    setEditingEntry(null);
    setFormState(defaultFormState);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (entry: LivingExpenseEntry) => {
    setEditingEntry(entry);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEntry(null);
    setFormState(defaultFormState);
  };

  const handleSave = async () => {
    const payload: Record<string, any> = {
      expenseCategory: formState.expenseCategory,
      description: formState.description || null,
      amount: parseFloat(formState.amount),
      frequency: formState.frequency,
      isJointExpense: formState.isJointExpense,
      hemApplicable: formState.hemApplicable,
    };

    if (editingEntry) {
      await updateMutation.mutateAsync({ id: editingEntry.id, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
  };

  // Calculate totals
  const totals = useMemo(() => {
    const totalAnnual = entries.reduce((sum, e) => sum + Number(e.annualAmount), 0);
    return { totalAnnual };
  }, [entries]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Loading state
  if (isLoading) {
    return (
      <Card data-testid="living-expenses-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="living-expenses-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Living Expenses</CardTitle>
            {entries.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleOpenAdd}>
                <Plus className="h-4 w-4 mr-1" />
                Add Expense
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            // Empty state
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Wallet className="h-10 w-10 text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground mb-2">No living expenses recorded</p>
              <p className="text-xs text-muted-foreground mb-6">
                Add monthly expenses for serviceability analysis
              </p>
              <Button onClick={handleOpenAdd}>
                <Plus className="h-4 w-4 mr-2" />
                Add Expense
              </Button>
            </div>
          ) : (
            // Table view
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Category</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Description</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Amount</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Frequency</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Annual</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="py-2 px-2">
                        <Badge variant="secondary" className="text-xs capitalize">
                          {CATEGORY_LABELS[entry.expenseCategory] || entry.expenseCategory}
                        </Badge>
                      </td>
                      <td className="py-2 px-2">{entry.description || '-'}</td>
                      <td className="py-2 px-2 text-right">{formatCurrency(Number(entry.amount))}</td>
                      <td className="py-2 px-2">{FREQUENCY_LABELS[entry.frequency] || entry.frequency}</td>
                      <td className="py-2 px-2 text-right">{formatCurrency(Number(entry.annualAmount))}</td>
                      <td className="py-2 px-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(entry)}
                            aria-label="edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteEntry(entry)}
                            aria-label="delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold" data-testid="living-expenses-totals">
                    <td className="py-2 px-2" colSpan={4}>Total Annual Expenses</td>
                    <td className="py-2 px-2 text-right">{formatCurrency(totals.totalAnnual)}</td>
                    <td className="py-2 px-2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => !open && handleCloseModal()}>
        <DialogContent className="max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Living Expense' : 'Add Living Expense'}</DialogTitle>
            <DialogDescription>
              {editingEntry ? 'Update the living expense details below' : 'Enter living expense details for this contact'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Expense Category */}
            <div className="space-y-2">
              <Label htmlFor="expenseCategory">Expense Category</Label>
              <select
                id="expenseCategory"
                name="expenseCategory"
                data-testid="expense-category-select"
                value={formState.expenseCategory}
                onChange={(e) => setFormState(prev => ({ ...prev, expenseCategory: e.target.value }))}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {EXPENSE_CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                name="description"
                value={formState.description}
                onChange={(e) => setFormState(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Rent - Main residence"
              />
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">Amount ($)</Label>
              <Input
                id="amount"
                name="amount"
                data-testid="amount-input"
                type="number"
                step="0.01"
                min="0"
                value={formState.amount}
                onChange={(e) => setFormState(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="e.g., 2500"
              />
            </div>

            {/* Annual Amount (read-only, auto-calculated) - placed before Frequency so /Annual/i matches label first */}
            <div className="space-y-2">
              <Label>Annual Amount</Label>
              <div
                data-testid="annual-amount-display"
                className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm font-medium"
              >
                {formatCurrency(calculatedAnnual)}
              </div>
            </div>

            {/* Frequency */}
            <div className="space-y-2">
              <Label htmlFor="frequency">Frequency</Label>
              <select
                id="frequency"
                name="frequency"
                data-testid="frequency-select"
                value={formState.frequency}
                onChange={(e) => setFormState(prev => ({ ...prev, frequency: e.target.value }))}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Joint Expense toggle */}
            <div className="flex items-center justify-between">
              <Label htmlFor="isJointExpense">Joint Expense</Label>
              <Switch
                id="isJointExpense"
                checked={formState.isJointExpense}
                onCheckedChange={(checked) => setFormState(prev => ({ ...prev, isJointExpense: checked }))}
              />
            </div>

            {/* HEM Applicable toggle */}
            <div className="flex items-center justify-between">
              <Label htmlFor="hemApplicable">HEM Applicable</Label>
              <Switch
                id="hemApplicable"
                checked={formState.hemApplicable}
                onCheckedChange={(checked) => setFormState(prev => ({ ...prev, hemApplicable: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editingEntry ? (
                'Save Changes'
              ) : (
                'Add Expense'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteEntry} onOpenChange={(open) => !open && setDeleteEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Living Expense</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {deleteEntry?.description || deleteEntry?.expenseCategory} expense? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteEntry && deleteMutation.mutate(deleteEntry.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default LivingExpensesPanel;
